import { useEffect, useId, useState } from "react";
import type { Choice } from "@proof/core";
import type { AssetLoader } from "./images";
import { AssetImage } from "./AssetImage";

const pageSize = 12;

/** Native radios keep keyboard behavior; only the visible page loads managed thumbnails. */
export function ImageChoices({
  id,
  label,
  options,
  value,
  required,
  disabled,
  system,
  helpId,
  invalid,
  assetUrl,
  loadAsset,
  onChange,
}: {
  id: string;
  label: string;
  options: Choice[];
  value: string;
  required: boolean;
  disabled: boolean;
  system: boolean;
  helpId: string;
  invalid: boolean;
  assetUrl: (id: string) => string;
  loadAsset?: AssetLoader;
  onChange: (value: string) => void;
}) {
  const name = useId(),
    [query, setQuery] = useState(""),
    [page, setPage] = useState(0);
  const search = query.trim().toLocaleLowerCase(),
    filtered = options.filter((option) =>
      option.label.toLocaleLowerCase().includes(search),
    ),
    count = Math.max(1, Math.ceil(filtered.length / pageSize)),
    current = Math.min(page, count - 1),
    visible = filtered.slice(current * pageSize, (current + 1) * pageSize),
    selected = options.find((option) => option.value === value);
  useEffect(() => {
    if (query) return;
    const index = options.findIndex((option) => option.value === value);
    if (index >= 0) setPage(Math.floor(index / pageSize));
  }, [value, options, query]);
  return (
    <fieldset
      id={id}
      className="image-choice-picker"
      role="radiogroup"
      aria-label={label}
      aria-required={required}
      aria-disabled={disabled}
      aria-invalid={invalid}
      aria-describedby={helpId}
      style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}
    >
      <legend style={{ fontWeight: 600, marginBottom: 8 }}>
        {label}
        {required && <span className="required-mark"> *</span>}
        {disabled && (
          <span className="field-tag">{system ? "System" : "Read only"}</span>
        )}
      </legend>
      {(options.length > pageSize || !!query) && (
        <label>
          Search {label} images
          <input
            type="search"
            value={query}
            maxLength={200}
            onKeyDown={(event) => {
              if (event.key === "Enter") event.preventDefault();
            }}
            onChange={(event) => {
              setQuery(event.target.value);
              setPage(0);
            }}
          />
        </label>
      )}
      <div
        className="image-choice-grid"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit,minmax(min(100%,140px),1fr))",
          gap: 8,
        }}
      >
        {visible.map((option) => {
          const assetId =
            option.assetId ||
            (/^asset_[a-zA-Z0-9_-]+$/.test(option.value)
              ? option.value
              : undefined);
          return (
            <label
              key={option.value}
              className="image-choice-card"
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 6,
                minWidth: 0,
                padding: 10,
                border: `2px solid ${value === option.value ? "#2b6657" : "#d7dfda"}`,
                borderRadius: 8,
                cursor: disabled ? "default" : "pointer",
                overflowWrap: "anywhere",
              }}
            >
              <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <input
                  type="radio"
                  name={name}
                  value={option.value}
                  aria-label={option.label}
                  aria-describedby={helpId}
                  aria-invalid={invalid}
                  disabled={disabled}
                  required={required && !selected}
                  checked={value === option.value}
                  onChange={() => onChange(option.value)}
                />
                <span>{option.label}</span>
              </span>
              {assetId ? (
                <AssetImage
                  assetId={assetId}
                  assetUrl={assetUrl}
                  loadAsset={loadAsset}
                  alt={option.label}
                  className="image-choice-preview"
                  style={{ width: "100%", height: 96, objectFit: "contain" }}
                />
              ) : (
                <span
                  style={{
                    display: "grid",
                    placeItems: "center",
                    height: 96,
                    background: "#f4f7f3",
                    fontSize: 12,
                  }}
                >
                  Image not assigned
                </span>
              )}
            </label>
          );
        })}
      </div>
      {!filtered.length && (
        <p role="status">
          {options.length
            ? "No matching images."
            : "No image options available."}
        </p>
      )}
      <p className="field-help">
        {selected ? `Selected: ${selected.label}` : "No image selected."}
      </p>
      {!required && (
        <button
          type="button"
          disabled={disabled || !value}
          onClick={() => onChange("")}
        >
          Clear {label} selection
        </button>
      )}
      {options.length > pageSize && (
        <nav aria-label={`${label} image pages`}>
          <p role="status">
            {filtered.length
              ? `Showing ${current * pageSize + 1}–${Math.min((current + 1) * pageSize, filtered.length)} of ${filtered.length} images`
              : "0 images"}
          </p>
          <button
            type="button"
            disabled={!current}
            onClick={() => setPage(current - 1)}
          >
            Previous images
          </button>{" "}
          <button
            type="button"
            disabled={current + 1 >= count}
            onClick={() => setPage(current + 1)}
          >
            Next images
          </button>
        </nav>
      )}
    </fieldset>
  );
}
